// store.jsx — server-backed state for the artist site.
//
// Reads come from GET /api/site once on mount. Writes go through the api
// helpers and then optimistically update local state; on failure we refetch.

import React from "react";
import { api, slugify } from "./api.js";

const empty = {
  sections: [],
  featuredWorkId: null,
  bio: { headline: "", location: "", body: "" },
  contact: { email: "", inquiries: "", representation: "", instagram: "", address: "" },
  tweaks: { direction: "gallery", paper: "white", accent: "#9a6a2b" },
  loading: true,
  error: null,
};

export function useSite() {
  const [state, setState] = React.useState(empty);

  const refresh = React.useCallback(async () => {
    try {
      const site = await api.getSite();
      setState({ ...site, loading: false, error: null });
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: e.message }));
    }
  }, []);

  React.useEffect(() => { refresh(); }, [refresh]);

  // Local optimistic update + remote write. Failures trigger a full refetch
  // so the UI converges back on truth instead of drifting silently.
  const mutate = React.useCallback(async (updater, remote) => {
    setState((s) => ({ ...updater(s), loading: false, error: null }));
    try { await remote(); }
    catch (e) {
      console.warn("write failed, refetching", e);
      await refresh();
    }
  }, [refresh]);

  const actions = React.useMemo(() => ({
    addSection: async (title) => {
      const { id } = await api.createSection(title);
      await refresh();
      return id;
    },
    removeSection: (sid) => mutate(
      (s) => ({ ...s, sections: s.sections.filter((x) => x.id !== sid) }),
      () => api.deleteSection(sid),
    ),
    updateSection: (sid, patch) => mutate(
      (s) => ({
        ...s,
        sections: s.sections.map((x) => {
          if (x.id !== sid) return x;
          const next = { ...x, ...patch };
          if ("title" in patch && !("slug" in patch)) next.slug = slugify(patch.title);
          return next;
        }),
      }),
      () => api.patchSection(sid, patch),
    ),
    reorderSections: (from, to) => mutate(
      (s) => {
        const arr = [...s.sections];
        const [m] = arr.splice(from, 1);
        arr.splice(to, 0, m);
        return { ...s, sections: arr };
      },
      () => {
        const ids = arrMove(state.sections.map((x) => x.id), from, to);
        return api.reorderSections(ids);
      },
    ),
    addWork: async (sid, file, meta = {}) => {
      const { id, image_key, image_aspect } = await api.createWork({
        section_id: sid,
        meta: {
          title: meta.title || (file ? file.name.replace(/\.[a-z0-9]+$/i, "") : "Untitled"),
          year: meta.year || String(new Date().getFullYear()),
          medium: meta.medium || "Oil on linen",
          dimensions: meta.dimensions || "",
          caption: meta.caption || "",
        },
        file,
      });
      await refresh();
      return { id, image_key, image_aspect };
    },
    updateWork: (sid, wid, patch, file) => mutate(
      (s) => ({
        ...s,
        sections: s.sections.map((x) => x.id === sid
          ? { ...x, works: x.works.map((w) => w.id === wid ? { ...w, ...patch } : w) }
          : x),
      }),
      async () => {
        const res = await api.patchWork(wid, patch, file);
        if (file) await refresh();
        return res;
      },
    ),
    removeWork: (sid, wid) => mutate(
      (s) => ({
        ...s,
        featuredWorkId: s.featuredWorkId === wid ? null : s.featuredWorkId,
        sections: s.sections.map((x) => x.id === sid
          ? { ...x, works: x.works.filter((w) => w.id !== wid),
                  coverWorkId: x.coverWorkId === wid ? (x.works.find((w) => w.id !== wid)?.id ?? null) : x.coverWorkId }
          : x),
      }),
      () => api.deleteWork(wid),
    ),
    reorderWorks: (sid, from, to) => mutate(
      (s) => ({
        ...s,
        sections: s.sections.map((x) => {
          if (x.id !== sid) return x;
          const arr = [...x.works];
          const [m] = arr.splice(from, 1);
          arr.splice(to, 0, m);
          return { ...x, works: arr };
        }),
      }),
      () => {
        const sec = state.sections.find((x) => x.id === sid);
        if (!sec) return Promise.resolve();
        const ids = arrMove(sec.works.map((w) => w.id), from, to);
        return api.reorderWorks(sid, ids);
      },
    ),
    setCover: (sid, wid) => mutate(
      (s) => ({ ...s, sections: s.sections.map((x) => x.id === sid ? { ...x, coverWorkId: wid } : x) }),
      () => api.patchSection(sid, { cover_work_id: wid }),
    ),
    setFeatured: (wid) => mutate(
      (s) => ({ ...s, featuredWorkId: wid }),
      () => api.setFeatured(wid),
    ),
    setBio: (patch) => mutate(
      (s) => ({ ...s, bio: { ...s.bio, ...patch } }),
      () => api.setBio({ ...state.bio, ...patch }),
    ),
    setContact: (patch) => mutate(
      (s) => ({ ...s, contact: { ...s.contact, ...patch } }),
      () => api.setContact({ ...state.contact, ...patch }),
    ),
    setTweaks: (patch) => mutate(
      (s) => ({ ...s, tweaks: { ...s.tweaks, ...patch } }),
      () => api.setTweaks({ ...state.tweaks, ...patch }),
    ),
    refresh,
  }), [mutate, refresh, state.bio, state.contact, state.sections, state.tweaks]);

  return [state, actions];
}

function arrMove(arr, from, to) {
  const out = [...arr];
  const [m] = out.splice(from, 1);
  out.splice(to, 0, m);
  return out;
}
